'use client';

import { useId, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { useTranslations } from 'next-intl';

import { ProductSection, SectionHead } from '@/components/product-section';
import { ProgressRail } from '@/components/ui/progress-rail';
import type { RailSegment } from '@/components/ui/progress-rail';
import { dur, spring, springFade } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

import './creative.css';

/**
 * "Önce fiyat, sonra üretim" — kredinin dört durağı.
 *
 * Akış ürünün kendisi (KREATIF-STUDYO-PLANI.md §2.6 "Kredi akışı", shared
 * `production-pricing.ts`): onaydan önce "en fazla X, tahmini Y"; onayda en
 * kötü durum kadar kredi AYRILIYOR; iş bitince sağlayıcının gerçek faturası
 * düşülüyor; kalanı iade. İş düşerse tamamı iade.
 *
 * Ray: `ProgressRail` (DESIGN.md §3 — doluluk okunan her yerde o). Ölçek
 * ayrılan kredi; durak değiştikçe dilimler değişiyor ve ray `key` ile yeniden
 * kuruluyor, kendi giriş taramasını tekrar oynuyor. Rakamlar örnek (15 sn'lik
 * bir videonun ölçülmüş tahmini ~460 kredi civarında; KSP §6).
 *
 * Durum hiçbir yerde yalnız rayla anlatılmıyor: her durağın yanında yazılı
 * rakam var, rayın `label`ı da aynı cümleyi ekran okuyucuya veriyor.
 */

const STOPS = ['quote', 'reserve', 'settle', 'refund'] as const;
type Stop = (typeof STOPS)[number];

const RESERVE = 520;
const ACTUAL = 438;

const SEGMENTS: Record<Stop, RailSegment[]> = {
  quote: [],
  reserve: [{ value: 1, color: 'var(--gray-400)' }],
  settle: [{ value: ACTUAL / RESERVE }, { value: (RESERVE - ACTUAL) / RESERVE, color: 'var(--gray-400)' }],
  refund: [{ value: ACTUAL / RESERVE }, { value: (RESERVE - ACTUAL) / RESERVE, color: 'var(--aqua-green-500)' }],
};

const copyV: Variants = {
  hidden: { opacity: 0, y: 6, filter: 'blur(3px)' },
  show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: springFade(spring.default) },
  exit: { opacity: 0, transition: { duration: dur.instant } },
};

export function CreativePrice() {
  const t = useTranslations('featuresCreative.price');
  const uid = useId();
  const [stop, setStop] = useState<Stop>('quote');
  const values = { reserve: RESERVE, actual: ACTUAL, refund: RESERVE - ACTUAL, estimate: 460 };

  return (
    <ProductSection tone="white" labelledBy={`${uid}-title`}>
      <div className="crp">
        <SectionHead
          id={`${uid}-title`}
          eyebrow={t('eyebrow')}
          title={t.rich('title', { hl })}
          lead={t('lead')}
        />

        <div className="crp-card">
          <ol className="crp-stops" aria-label={t('stopsLabel')}>
            {STOPS.map((key, i) => (
              <li key={key}>
                <button
                  type="button"
                  className="crp-stop"
                  aria-pressed={stop === key}
                  aria-controls={`${uid}-read`}
                  onClick={() => setStop(key)}
                >
                  <span className="crp-stop-num">{i + 1}</span>
                  {t(`stops.${key}.name`)}
                </button>
              </li>
            ))}
          </ol>

          <div className="crp-rail">
            <ProgressRail
              key={stop}
              segments={SEGMENTS[stop]}
              label={t(`stops.${stop}.figure`, values)}
            />
          </div>

          <div className="crp-read" id={`${uid}-read`} aria-live="polite">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={stop}
                variants={copyV}
                initial="hidden"
                animate="show"
                exit="exit"
              >
                <p className="crp-figure">{t(`stops.${stop}.figure`, values)}</p>
                <p className="crp-text">{t(`stops.${stop}.text`)}</p>
              </motion.div>
            </AnimatePresence>
          </div>

          <p className="crp-foot">{t('foot')}</p>
        </div>
      </div>

      <ul className="ps-facts">
        {(['real', 'fail', 'auto'] as const).map((key) => (
          <li key={key} className="ps-fact">
            <p className="ps-fact-title">{t(`facts.${key}.title`)}</p>
            <p className="ps-fact-text">{t(`facts.${key}.text`)}</p>
          </li>
        ))}
      </ul>
    </ProductSection>
  );
}
